/** @format */
import { Injectable } from '@angular/core';
import { PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    CourseUnitSelection,
    CustomStudyDraft,
    LocalId,
    OtmId,
    Plan,
    PlanValidationState,
    StudyPeriod,
    StudyRight,
} from 'common-typescript/types';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CurrentStudyPeriodService, PrimaryPlanService } from 'sis-components';
import { PlanCourseUnitSelectionService } from './plan-course-unit-selection.service';
import { PlanLoaderService } from './plan-loader.service';
import { StudyTimingValidationResult, StudyTimingValidationService } from './study-timing-validation.service';

@Injectable({ providedIn: 'root' })
export class StudyRightExtensionRequirementsService {
    constructor(
        private readonly _primaryPlanService: PrimaryPlanService,
        private readonly _planCourseUnitSelectionService: PlanCourseUnitSelectionService,
        private readonly _currentStudyPeriodService: CurrentStudyPeriodService,
        private readonly _studyTimingValidationService: StudyTimingValidationService,
        private readonly _planLoaderService: PlanLoaderService,
    ) {}

    /**
     * Returns requirement data for applying for extension for the given study right.
     */
    getStudyRightExtensionRequirements(studyRight: StudyRight): Observable<StudyRightExtensionRequirements | null> {
        return this._primaryPlanService.getPrimaryPlanForStudyRight(studyRight).pipe(
            switchMap((primaryPlan: Plan | null) => {
                if (!primaryPlan) {
                    return of(null);
                }
                // Get ValidatablePlan.plan because it is the same plan that is displayed in the user interface.
                // It includes completed course units regardless of version (fetched by groupId) in plan.courseUnitSelections.
                // With this we will know if the user has completed any version of a course unit, and it no longer needs timing.
                return this._planLoaderService.createValidatablePlan(primaryPlan.id, studyRight.id);
            }),
            switchMap((validatablePlan: ValidatablePlan | null) => {
                if (!validatablePlan) {
                    return of(null);
                }
                return this.getTimingFields(validatablePlan.plan).pipe(
                    map(
                        (timingFields: Partial<StudyRightExtensionRequirements>) =>
                            <StudyRightExtensionRequirements>{
                                primaryPlan: validatablePlan.plan,
                                planState: PlanValidationTs.validatePlan(validatablePlan).rootValidationResult?.state ?? null,
                                ...timingFields,
                            },
                    ),
                );
            }),
        );
    }

    private getTimingFields(plan: Plan): Observable<Partial<StudyRightExtensionRequirements>> {
        return combineLatest([
            this._planCourseUnitSelectionService.getCourseUnitSelectionsRequiringTiming(plan),
            this._currentStudyPeriodService.currentStudyPeriod$,
        ]).pipe(
            map(([courseUnitSelectionsRequiringTiming, currentStudyPeriod]: [CourseUnitSelection[], StudyPeriod | null]) => {
                // validate course unit selection timing
                let courseUnitIdsWithNoTiming: OtmId[];
                let courseUnitIdsWithTimingInThePast: OtmId[];
                if (courseUnitSelectionsRequiringTiming.length) {
                    const courseUnitSelectionTimingValidationResult: StudyTimingValidationResult<CourseUnitSelection> =
                        this._studyTimingValidationService.validateStudyTimings(courseUnitSelectionsRequiringTiming, currentStudyPeriod);
                    courseUnitIdsWithNoTiming = courseUnitSelectionTimingValidationResult.studiesWithNoTiming.map(cus => cus.courseUnitId);
                    courseUnitIdsWithTimingInThePast = courseUnitSelectionTimingValidationResult.studiesWithTimingInThePast.map(
                        cus => cus.courseUnitId,
                    );
                } else {
                    courseUnitIdsWithNoTiming = [];
                    courseUnitIdsWithTimingInThePast = [];
                }

                // validate custom study draft timing
                let customStudyDraftIdsWithNoTiming: LocalId[];
                let customStudyDraftIdsWithTimingInThePast: LocalId[];
                if (plan.customStudyDrafts?.length) {
                    const customStudyDraftTimingValidationResult: StudyTimingValidationResult<CustomStudyDraft> =
                        this._studyTimingValidationService.validateStudyTimings(plan.customStudyDrafts, currentStudyPeriod);
                    customStudyDraftIdsWithNoTiming = customStudyDraftTimingValidationResult.studiesWithNoTiming.map(csd => csd.id);
                    customStudyDraftIdsWithTimingInThePast = customStudyDraftTimingValidationResult.studiesWithTimingInThePast.map(
                        csd => csd.id,
                    );
                } else {
                    customStudyDraftIdsWithNoTiming = [];
                    customStudyDraftIdsWithTimingInThePast = [];
                }

                return <Partial<StudyRightExtensionRequirements>>{
                    courseUnitIdsWithNoTiming,
                    courseUnitIdsWithTimingInThePast,
                    customStudyDraftIdsWithNoTiming,
                    customStudyDraftIdsWithTimingInThePast,
                };
            }),
        );
    }
}

export interface StudyRightExtensionRequirements {
    readonly primaryPlan: Plan;
    readonly planState: PlanValidationState | null;
    readonly courseUnitIdsWithNoTiming: readonly OtmId[];
    readonly courseUnitIdsWithTimingInThePast: readonly OtmId[];
    readonly customStudyDraftIdsWithNoTiming: readonly LocalId[];
    readonly customStudyDraftIdsWithTimingInThePast: readonly LocalId[];
}
